import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { COLORS } from '../../theme';
import { PUSH_BENEFITS } from './notificationVisuals';

const TITULO_PADRAO = 'Com as notificações ligadas você recebe:';
const CAUDA = 12;

/**
 * “Balão” de fala com os benefícios das notificações push.
 *
 * Usado no pedido diário de permissão (área do cliente) e no card de
 * Configurações. Usa apenas tokens do tema (`COLORS` + `palette`) para manter
 * o mesmo visual no modo claro e no escuro — nada de branco fixo.
 */
const NotificationBenefitsBalloon = ({ titulo = TITULO_PADRAO, itens = PUSH_BENEFITS, sx }) => {
  const theme = useTheme();
  const escuro = theme.palette.mode === 'dark';
  const fundo = alpha(COLORS.orange, escuro ? 0.18 : 0.08);
  const borda = `1px solid ${COLORS.orangeBorderSoft}`;

  return (
    <Box
      role="note"
      aria-label={titulo}
      sx={[
        {
          position: 'relative',
          mt: 2.5,
          px: 1.75,
          py: 1.5,
          borderRadius: '16px',
          bgcolor: fundo,
          border: borda,
          // Cauda do balão: quadrado girado, com a borda só nos lados visíveis.
          '&::after': {
            content: '""',
            position: 'absolute',
            top: -CAUDA / 2,
            left: 28,
            width: CAUDA,
            height: CAUDA,
            bgcolor: fundo,
            borderTop: borda,
            borderLeft: borda,
            transform: 'rotate(45deg)',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography
        variant="caption"
        component="p"
        sx={{
          m: 0,
          mb: 1,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'primary.main',
        }}
      >
        {titulo}
      </Typography>

      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
        {itens.map(({ id, titulo: tituloItem, descricao, Icon, color }) => {
          const corDoIcone = color || COLORS.orange;

          return (
            <Box component="li" key={id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box
                aria-hidden
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '9px',
                  bgcolor: alpha(corDoIcone, 0.16),
                  color: corDoIcone,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  mt: '1px',
                }}
              >
                <Icon sx={{ fontSize: 15 }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {tituloItem}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="p"
                  sx={{ m: 0, lineHeight: 1.35 }}
                >
                  {descricao}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

NotificationBenefitsBalloon.propTypes = {
  /** Rótulo do balão (também é o nome acessível do `role="note"`). */
  titulo: PropTypes.string,
  /** Lista de benefícios: `{ id, titulo, descricao, Icon, color }`. */
  itens: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      titulo: PropTypes.string.isRequired,
      descricao: PropTypes.string,
      Icon: PropTypes.elementType.isRequired,
      color: PropTypes.string,
    })
  ),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default NotificationBenefitsBalloon;
